
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MasterLicenseLayout from '@/components/masterlicense/MasterLicenseLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Search, RefreshCw, Plus, Database, FileText, Shield } from 'lucide-react';
import { useMasterLicense } from '@/hooks/useMasterLicense';
import { ReferralId } from '@/utils/referral/bank/types';
import ReferralIdReportModal from '@/components/superadmin/ReferralIdReportModal';

const MasterLicenseReferralBank = () => {
  const navigate = useNavigate();
  const {
    isLoading: isMasterLoading, 
    handleLogout,
    referralIds,
    bankStats,
    isGenerating,
    searchQuery,
    setSearchQuery,
    handleGenerateMoreIds,
    handleCheckAndReplenish,
    refreshData
  } = useMasterLicense();
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  
  // Modal state for detailed report
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  
  // Calculate pagination
  const totalPages = Math.ceil(referralIds.length / itemsPerPage);
  const paginatedIds = referralIds.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  
  const handlePreviousPage = () => {
    setCurrentPage(current => Math.max(current - 1, 1));
  };
  
  const handleNextPage = () => {
    setCurrentPage(current => Math.min(current + 1, totalPages));
  };
  
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Filtering is already done in real-time
    // Reset to first page when searching
    setCurrentPage(1);
  };
  
  // Loading state
  if (isMasterLoading) {
    return (
      <MasterLicenseLayout
        isLoading={true}
        onLogout={handleLogout}
        activeTab="referral-bank"
      >
        <div className="flex items-center justify-center p-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading referral ID bank...</p>
          </div>
        </div>
      </MasterLicenseLayout>
    );
  }
  
  return (
    <MasterLicenseLayout
      isLoading={false}
      onLogout={handleLogout}
      activeTab="referral-bank"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Shield className="mr-2 h-6 w-6 text-indigo-600" />
            Referral ID Bank Management
          </h1>
          <p className="text-gray-600">
            Manage the pool of available referral IDs for all instances
          </p>
          <div className="mt-2 bg-indigo-50 p-3 rounded-md border border-indigo-100">
            <p className="text-indigo-700 text-sm">
              <strong>Master Admin Exclusive:</strong> Only Master Admins can generate new referral IDs. Super Admins can view and allocate existing IDs, but cannot create new ones.
            </p>
          </div>
        </div>
        
        {/* Bank Statistics Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Referral ID Bank Statistics</CardTitle>
            <CardDescription>
              Overview of the referral ID bank and allocation status
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                <h3 className="text-gray-500 text-sm font-medium">Total Referral IDs</h3>
                <p className="text-3xl font-bold text-gray-900">{bankStats?.totalIds || 0}</p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                <h3 className="text-gray-500 text-sm font-medium">Available IDs</h3>
                <p className="text-3xl font-bold text-green-600">{bankStats?.unassignedIds || 0}</p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                <h3 className="text-gray-500 text-sm font-medium">Assigned IDs</h3>
                <p className="text-3xl font-bold text-blue-600">{bankStats?.assignedIds || 0}</p>
              </div>
            </div>
            
            <div className="mt-6 flex flex-wrap gap-3">
              <Button 
                onClick={() => handleGenerateMoreIds(1000)} 
                disabled={isGenerating}
                className="flex items-center"
              >
                {isGenerating ? (
                  <div className="h-4 w-4 border-2 border-current border-opacity-25 border-t-current rounded-full animate-spin mr-2"></div>
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                Generate 1000 More IDs
              </Button>
              
              <Button 
                variant="outline" 
                onClick={handleCheckAndReplenish}
                className="flex items-center"
              >
                <Database className="h-4 w-4 mr-2" />
                Check & Replenish Bank
              </Button>
              
              <Button 
                variant="outline" 
                onClick={refreshData}
                className="flex items-center"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh Data
              </Button>
              
              <Button 
                variant="outline"
                onClick={() => setIsReportModalOpen(true)}
                className="flex items-center"
              >
                <FileText className="h-4 w-4 mr-2" />
                Detailed Assignment Report
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* Referral ID List */}
        <Card>
          <CardHeader>
            <CardTitle>Referral ID List</CardTitle>
            <CardDescription>
              View and search all available referral IDs in the bank
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Search Form */}
            <form onSubmit={handleSearch} className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="search"
                  placeholder="Search by referral code or instance ID..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </form>
            
            {/* Referral IDs Table */}
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Referral Code</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Instance ID</TableHead>
                    <TableHead>Created At</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedIds.length > 0 ? (
                    paginatedIds.map((referralId: ReferralId) => (
                      <TableRow key={referralId.id}>
                        <TableCell className="font-mono font-medium">
                          {referralId.code}
                        </TableCell>
                        <TableCell>
                          {referralId.isAssigned ? (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              Assigned
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              Available
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {referralId.instanceId || '-'}
                        </TableCell>
                        <TableCell>
                          {new Date(referralId.createdAt).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} className="h-24 text-center">
                        No referral IDs found
                        {searchQuery && " matching your search"}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-gray-500">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, referralIds.length)} of {referralIds.length} results
                </p>
                <div className="flex items-center space-x-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Detailed Assignment Report Modal */}
      <ReferralIdReportModal 
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
      />
    </MasterLicenseLayout>
  );
};

export default MasterLicenseReferralBank;
